import {
  ClockIcon,
  DevicePhoneMobileIcon,
  LanguageIcon,
  PuzzlePieceIcon
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useLanguageContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { useSystemQuery } from '../../hooks/use-system-query'
import { useAnswersBySpaceQuery } from '../../hooks/useAnswersBySpaceQuery'
import Panel from '../tailwind/Panel'
import SectionHeading from '../tailwind/SectionHeading'
import ShowMoreToggle from '../tailwind/ShowMoreToggle'

const INITIAL_COUNT = 4

export default function AssessmentMonitor({ space }) {
  const { language } = useLanguageContext()
  const { partner } = usePartnerContext()
  const { cognitoUser } = useUserContext()
  const { t } = useTranslation()

  const [expanded, setExpanded] = useState(false)

  const { data: answers } = useAnswersBySpaceQuery({
    spaceId: space?.id
  })
  const { data: system } = useSystemQuery({ username: cognitoUser?.username })

  // Check if Assessment Monitor is visible based on partner settings
  if (!partner.isDashboardComponentVisible?.assessmentMonitor) {
    return null
  }

  const challenges = system?.challenges?.data
    ? Object.values(JSON.parse(system.challenges.data))
    : []

  function formatTime(ms, t) {
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    if (minutes === 0 && seconds === 0)
      return t('talent.assessment_monitor.no_data')
    return `${minutes} min ${seconds} sek`
  }

  const renderBody = () => {
    if (!system?.challenges?.data) {
      return (
        <p className='text-sm text-gray-500'>
          {t('talent.assessment_monitor.loading')}
        </p>
      )
    }

    if (!answers || answers.length === 0) {
      return (
        <div className='rounded-lg bg-white p-6 text-center shadow-sm ring-1 ring-gray-200'>
          <p className='text-sm text-gray-600'>
            {t('talent.assessment_monitor.no_data_yet')}
          </p>
        </div>
      )
    }

    const canCollapse = answers.length > INITIAL_COUNT
    const visibleAnswers =
      !canCollapse || expanded ? answers : answers.slice(0, INITIAL_COUNT)
    const hiddenCount = answers.length - INITIAL_COUNT

    return (
      <>
        <ul role='list' className='space-y-3'>
          {visibleAnswers.map((answer) => {
            const assessment = challenges.find(
              (challenge) => challenge.exam_id === answer.exam_id
            )

            return (
              <li
                key={answer.id}
                className='overflow-hidden rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200'
              >
                <h3 className='text-sm font-semibold text-gray-900'>
                  {`${assessment?.title?.[language] || t('talent.assessment_monitor.assessment')}`}
                </h3>
                <dl className='mt-3 grid grid-cols-2 gap-x-4 gap-y-2'>
                  <dt className='flex items-center gap-1.5 text-sm text-gray-500'>
                    <ClockIcon className='size-4 flex-shrink-0 text-primaryBlue' />
                    {t('talent.assessment_monitor.time_needed')}
                  </dt>
                  <dd className='text-sm text-gray-900'>
                    {formatTime(answer.total_time_needed_in_ms, t)}
                  </dd>
                  <dt className='flex items-center gap-1.5 text-sm text-gray-500'>
                    <PuzzlePieceIcon className='size-4 flex-shrink-0 text-primaryBlue' />
                    {t('talent.assessment_monitor.attempts')}
                  </dt>
                  <dd className='text-sm text-gray-900'>
                    {answer.attempts || 1}
                  </dd>
                  <dt className='flex items-center gap-1.5 text-sm text-gray-500'>
                    <LanguageIcon className='size-4 flex-shrink-0 text-primaryBlue' />
                    {t('talent.assessment_monitor.language')}
                  </dt>
                  <dd className='text-sm text-gray-900'>
                    {t(`language.${answer.language || 'de'}`)}
                  </dd>
                  <dt className='flex items-center gap-1.5 text-sm text-gray-500'>
                    <DevicePhoneMobileIcon className='size-4 flex-shrink-0 text-primaryBlue' />
                    {t('talent.assessment_monitor.operating_system')}
                  </dt>
                  <dd className='text-sm text-gray-900'>{answer.os}</dd>
                </dl>
              </li>
            )
          })}
        </ul>
        {canCollapse && (
          <ShowMoreToggle
            expanded={expanded}
            onToggle={() => setExpanded(!expanded)}
            moreLabel={t('pages.talent.list_show_more', { count: hiddenCount })}
            lessLabel={t('pages.talent.show_less')}
          />
        )}
      </>
    )
  }

  return (
    <Panel>
      <section aria-labelledby='assessment-monitor-title'>
        <SectionHeading
          id='assessment-monitor-title'
          title={t('talent.assessment_monitor.title')}
          description={t('talent.assessment_monitor.description')}
        />
        <div className='border-t border-gray-300 pt-5'>{renderBody()}</div>
      </section>
    </Panel>
  )
}

AssessmentMonitor.propTypes = {
  space: PropTypes.object
}
